import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { getInitials } from '../../utils/helpers';

/**
 * LeadSearchCombobox — command-palette style lead search.
 *
 * UX contract (fixes the old full-screen SearchResultsModal that covered the
 * input while typing):
 * - The dropdown renders ANCHORED below the input (`absolute top-full`), so the
 *   text being typed stays visible at all times. Nothing ever covers the input.
 * - Results appear from the FIRST character — no artificial 2-char gate.
 * - Full keyboard support: ↑/↓ move, Enter opens, Esc closes (and clears when
 *   already closed-but-filled), Home/End jump. Focus ring stays on the input.
 * - Matching is delegated to a caller-supplied matcher — the SAME function the
 *   leads grid uses — so dropdown results and grid filtering can never disagree.
 * - Result list is capped; the footer shows the live match count.
 */

/** A row in the combobox dropdown. All display fields are pre-formatted. */
export interface LeadSearchItem {
  id: string;
  /** Primary line — lead name. */
  title: string;
  /** Secondary line — "Role at Company". */
  subtitle?: string;
  /** Right-aligned status label. */
  statusLabel?: string;
  /** Avatar URL (already proxied) or undefined to render initials. */
  avatarUrl?: string;
  /** Location / campaign meta shown under the subtitle. */
  meta?: string;
  /** Original record — passed through on selection. */
  lead: unknown;
}

/** A highlighted fragment of a display string. */
interface MatchPart {
  text: string;
  /** True when this fragment matched the query (rendered with highlight). */
  match: boolean;
}

interface LeadSearchComboboxProps {
  /** Current query. Controlled by the parent (drives grid filtering too). */
  value: string;
  onChange: (next: string) => void;
  /** Caller-supplied matcher — the same one the leads grid uses. */
  matcher: (lead: unknown, query: string) => boolean;
  /** Full lead universe to search (NOT the already-filtered list). */
  items: unknown[];
  /** Called when the user picks a result (or presses Enter). */
  onSelect: (lead: unknown) => void;
  /** Scope chip text, e.g. "Campaign: Northstar" or "All Campaigns". */
  scopeLabel?: string;
  /** Max rows rendered before the "+N more" footer kicks in. */
  maxResults?: number;
  /** Accessible label for the input. */
  ariaLabel?: string;
  /** Extra classes on the root (width control lives with the caller). */
  className?: string;
}

/** Split `text` into matched/unmatched fragments for highlight rendering. */
function highlightParts(text: string, query: string): MatchPart[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [{ text, match: false }];

  const parts: MatchPart[] = [];
  const lowerText = text.toLowerCase();
  let cursor = 0;
  let index = lowerText.indexOf(normalizedQuery, cursor);
  while (index !== -1) {
    if (index > cursor) parts.push({ text: text.slice(cursor, index), match: false });
    parts.push({ text: text.slice(index, index + normalizedQuery.length), match: true });
    cursor = index + normalizedQuery.length;
    index = lowerText.indexOf(normalizedQuery, cursor);
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
  return parts;
}

const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  const parts = useMemo(() => highlightParts(text, query), [text, query]);
  if (parts.length === 1 && !parts[0].match) return <>{text}</>;
  return (
    <>
      {parts.map((part, index) =>
        part.match ? (
          <mark key={index} className="rounded bg-[#5260FF]/25 px-0.5 text-[#939dff]">
            {part.text}
          </mark>
        ) : (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        )
      )}
    </>
  );
};

/**
 * Avatar for a dropdown row: picture when it loads, initials when the URL is
 * missing OR fails (dead/blocked URLs must never show the broken-image glyph —
 * same behavior as the leads grid cards).
 */
const LeadResultAvatar = ({ item }: { item: LeadSearchItem }) => {
  const [errored, setErrored] = useState(false);
  // A different lead reusing this row resets the error state.
  useEffect(() => { setErrored(false); }, [item.id, item.avatarUrl]);

  const showImage = item.avatarUrl && !errored;
  return showImage ? (
    <img
      src={item.avatarUrl}
      alt=""
      loading="lazy"
      onError={() => setErrored(true)}
      className="h-9 w-9 flex-shrink-0 rounded-full border border-white/10 object-cover"
    />
  ) : (
    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#5260FF] to-[#15FBFF] text-xs font-semibold text-white">
      {getInitials(item.title)}
    </span>
  );
};

export default function LeadSearchCombobox({
  value,
  onChange,
  matcher,
  items,
  onSelect,
  scopeLabel,
  maxResults = 8,
  ariaLabel = 'Search leads',
  className = '',
}: LeadSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  /** Filter + cap. useMemo keeps typing at 60fps on 10k-lead campaigns. */
  const results = useMemo(() => {
    const query = value.trim();
    if (!query) return [];
    return items.filter((item) => matcher(item, query)).slice(0, maxResults);
  }, [items, matcher, value, maxResults]);

  const totalMatches = useMemo(() => {
    const query = value.trim();
    if (!query) return 0;
    return items.reduce<number>((count, item) => count + (matcher(item, query) ? 1 : 0), 0);
  }, [items, matcher, value]);

  // Close on outside click — same pattern as the filter dropdowns.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  // Close on Escape at the document level too (covers focus in dropdown list).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const closeAndClear = useCallback(() => {
    setOpen(false);
    setActiveIndex(0);
  }, []);

  const handleSelect = useCallback(
    (item: unknown) => {
      closeAndClear();
      // Clear the query too — selection is a jump-to action, and a lingering
      // term would keep the grid filtered after the user closes the lead.
      onChange('');
      onSelect((item as LeadSearchItem).lead);
    },
    [closeAndClear, onChange, onSelect]
  );

  const handleInputChange = (next: string) => {
    onChange(next);
    setActiveIndex(0);
    if (next.trim()) setOpen(true);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          if (value.trim()) setOpen(true);
          return;
        }
        setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(Math.max(results.length - 1, 0));
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (open && results[activeIndex]) {
          handleSelect(results[activeIndex]);
        } else if (value.trim()) {
          setOpen(true);
        }
        break;
      case 'Escape':
        if (open) {
          event.stopPropagation();
          closeAndClear();
        } else if (value) {
          onChange('');
        }
        break;
      default:
        break;
    }
  };

  /** Scroll the active option into view as ↑/↓ moves. */
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="relative">
        <Icon
          icon="bx:search-alt-2"
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search name, company, role…"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (value.trim()) setOpen(true);
          }}
          onKeyDown={handleInputKeyDown}
          className="h-9 w-full rounded-xl border border-white/6 bg-[#0a0c11] py-1.5 pl-10 pr-10 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors placeholder:text-gray-500 hover:border-[#5260FF]/45 hover:bg-[#0e1017] focus:border-[#5260FF]/45 focus:bg-[#0e1017] focus:outline-none"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              closeAndClear();
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 transition-colors hover:text-white"
          >
            <Icon icon="bx:x" className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={`${ariaLabel} results`}
          className="absolute left-0 top-full z-50 mt-2 w-[360px] max-w-[calc(100vw-2rem)] max-h-[min(26rem,calc(100vh-12rem))] overflow-y-auto rounded-xl border border-white/10 bg-[#0c0e14] shadow-[0_24px_64px_rgba(0,0,0,0.5)] custom-scrollbar"
        >
          {scopeLabel && (
            <div className="border-b border-white/8 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
              {scopeLabel}
            </div>
          )}

          {results.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <Icon icon="bx:search-alt-2" className="h-8 w-8 text-gray-600" aria-hidden />
              <p className="text-sm font-medium text-white">No matching leads</p>
              <p className="text-xs text-gray-500">Try a name, company, role, or location</p>
            </div>
          ) : (
            <ul className="py-1.5">
              {results.map((item, index) => {
                const lead = item as LeadSearchItem;
                const isActive = index === activeIndex;
                return (
                  <li key={lead.id} role="option" aria-selected={isActive}>
                    <button
                      type="button"
                      data-active={isActive || undefined}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => handleSelect(item)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        isActive ? 'bg-[#5260FF]/12' : 'hover:bg-white/[0.04]'
                      }`}
                    >
                      <LeadResultAvatar item={lead} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-white" title={lead.title}>
                          <HighlightedText text={lead.title} query={value} />
                        </span>
                        {lead.subtitle && (
                          <span className="block truncate text-xs text-gray-400" title={lead.subtitle}>
                            <HighlightedText text={lead.subtitle} query={value} />
                          </span>
                        )}
                        {lead.meta && (
                          <span className="mt-0.5 block truncate text-[11px] text-gray-500">
                            <HighlightedText text={lead.meta} query={value} />
                          </span>
                        )}
                      </span>
                      {lead.statusLabel && (
                        <span className="flex-shrink-0 rounded-md bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-gray-300">
                          {lead.statusLabel}
                        </span>
                      )}
                      <Icon
                        icon="bx:chevron-right"
                        className={`h-4 w-4 flex-shrink-0 transition-colors ${isActive ? 'text-[#939dff]' : 'text-white/20'}`}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {results.length > 0 && (
            <div className="border-t border-white/8 px-4 py-2 text-[11px] text-gray-500">
              {totalMatches > results.length
                ? `Showing ${results.length} of ${totalMatches.toLocaleString()} matches — keep typing to narrow`
                : `${totalMatches.toLocaleString()} ${totalMatches === 1 ? 'match' : 'matches'}`}
              <span className="ml-2 hidden text-gray-600 sm:inline">· ↑↓ navigate · Enter opens · Esc closes</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
